import { useState, useRef } from 'react';
import { 
    Grid, 
    Box, 
    Typography, 
    Button, 
    TextField,
    CircularProgress,
    Autocomplete
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { Colors } from '../../../../../styles/theme';
import { useLocation } from 'hooks/useLocation';
import { ValidDataService } from 'service/validDataService';

export default function FrameSection({ 
    frameData, 
    inProgress,
    getFrameByModelNo,
    setFrameSelectDialogOpen,
    onFrameDataChange,
    onFrameSelect
}) {
    const { selectedLocation } = useLocation();
    const [searchOptions, setSearchOptions] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const searchTimerRef = useRef(null);
    const abortControllerRef = useRef(null);

    const triggerSearch = (query) => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        if (abortControllerRef.current) abortControllerRef.current.abort();

        if (!query || query.length < 2) {
            setSearchOptions([]);
            return;
        }

        searchTimerRef.current = setTimeout(async () => {
            abortControllerRef.current = new AbortController();
            setSearchLoading(true);
            try {
                const result = await ValidDataService.searchFrameInventory(
                    { q: query, locationId: selectedLocation?.id, limit: 20 },
                    abortControllerRef.current.signal
                );
                if (result?.code === 0 && Array.isArray(result.data)) {
                    setSearchOptions(result.data);
                } else {
                    setSearchOptions([]);
                }
            } catch (err) {
                if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
                    setSearchOptions([]);
                }
            } finally {
                setSearchLoading(false);
            }
        }, 400);
    };

    return (
        <Grid container spacing={1} p={1} boxShadow={2} sx={{ mb: 2, border: '1px solid #ccc', borderRadius: 1 }}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', p: 1, backgroundColor: Colors.secondary }}>
                    <Typography sx={{ flexGrow: 1 }}>Frame</Typography>
                    <Button size='small' variant='contained' startIcon={<Search />} onClick={() => setFrameSelectDialogOpen(true)}>Add Frame</Button>
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Autocomplete
                    freeSolo
                    options={searchOptions}
                    loading={searchLoading}
                    filterOptions={(x) => x}
                    getOptionLabel={(option) =>
                        typeof option === 'string' ? option : (option.modelNumber || '')
                    }
                    inputValue={frameData.modelNumber || ''}
                    onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'input') {
                            onFrameDataChange && onFrameDataChange('modelNumber', newInputValue.toUpperCase());
                            triggerSearch(newInputValue);
                        } else if (reason === 'clear') {
                            onFrameDataChange && onFrameDataChange('modelNumber', '');
                            setSearchOptions([]);
                        }
                    }}
                    onChange={(event, newValue) => {
                        if (newValue && typeof newValue !== 'string' && onFrameSelect) {
                            // Prevent selection if quantity is 0
                            if (newValue.quantityOnHand === 0) {
                                return;
                            }
                            onFrameSelect(newValue);
                        }
                    }}
                    renderOption={(props, option) => {
                        const color = option.properties?.frameColor;
                        const size = option.properties?.sizeCode;
                        const quantityOnHand = option.quantityOnHand;
                        const brand = option.brand?.name;
                        const supplier = option.supplier?.supplierName;
                        const price = option.listPrice != null
                            ? `$${parseFloat(option.listPrice).toFixed(2)}`
                            : null;
                        const detail = [color, size].filter(Boolean).join(', ');
                        const meta = [brand, supplier].filter(Boolean).join(' · ');
                        const isOutOfStock = quantityOnHand === 0;
                        
                        return (
                            <Box 
                                component="li" 
                                {...props} 
                                key={option.id || option.modelNumber}
                                sx={{
                                    opacity: isOutOfStock ? 0.5 : 1,
                                    pointerEvents: isOutOfStock ? 'none' : 'auto',
                                    cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                                    ...props.sx
                                }}
                            >
                                <Box>
                                    <Typography 
                                        variant="body2" 
                                        fontWeight="bold" 
                                        sx={{ 
                                            textTransform: 'uppercase',
                                            color: isOutOfStock ? 'text.disabled' : 'inherit'
                                        }}
                                    >
                                        {option.modelNumber}
                                        {isOutOfStock && ' (Out of Stock)'}
                                    </Typography>
                                    <Typography 
                                        variant="caption" 
                                        color={isOutOfStock ? 'text.disabled' : 'text.secondary'}
                                    >
                                        {[meta, detail, price, quantityOnHand != null ? `Qty: ${quantityOnHand}` : null].filter(Boolean).join(' | ')}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Model #"
                            variant="outlined"
                            size="small"
                            inputProps={{ ...params.inputProps, style: { textTransform: 'uppercase' } }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {searchLoading
                                            ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                                            : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }}
                        />
                    )}
                />
            </Grid>
            {/* <Grid item xs={6}>
                <TextField
                    fullWidth
                    label="Supplier"
                    variant="outlined"
                    size="small"
                    disabled
                    InputLabelProps={{ shrink: true }}
                    value={frameData.supplier?.supplierName || ''}
                    onChange={(e) => onFrameDataChange && onFrameDataChange('supplier.supplierName', e.target.value)}
                />
            </Grid> */}
            <Grid item xs={6}>
                <TextField
                    fullWidth
                    label="Brand"
                    variant="outlined"
                    size="small"
                    disabled
                    InputLabelProps={{ shrink: true }}
                    value={frameData.brand?.name || ''}
                    onChange={(e) => onFrameDataChange && onFrameDataChange('brand.name', e.target.value)}
                />
            </Grid>
            {/* <Grid item xs={4}>
                <TextField
                    fullWidth
                    label="Frame Color"
                    variant="outlined"
                    size="small"
                    disabled
                    value={frameData.properties?.frameColor || ''}
                    onChange={(e) => onFrameDataChange && onFrameDataChange('properties.frameColor', e.target.value)}
                />
            </Grid> */}
            <Grid item xs={2}>
                <TextField
                    fullWidth
                    label="Color"
                    variant="outlined"
                    size="small"
                    disabled
                    value={frameData.properties?.frameColorCode || frameData.properties?.frameColor  || ''}
                    onChange={(e) => onFrameDataChange && onFrameDataChange('properties.frameColorCode', e.target.value)}
                />
            </Grid>
            {/* <Grid item xs={4}>
                <TextField
                    label="Material"
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    value={frameData.properties?.material || ''}
                    onChange={(e) => onFrameDataChange && onFrameDataChange('properties.material', e.target.value)}
                />
            </Grid> */}
            <Grid item xs={4}>
                <TextField
                    label="Rim Type"
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    value={frameData.properties?.rimType || ''}
                    onChange={(e) => onFrameDataChange && onFrameDataChange('properties.rimType', e.target.value)}
                />
            </Grid>
            {/* <Grid item xs={4}>
                <TextField
                    label="Shape"
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    value={frameData.properties?.shape || ''}
                    onChange={(e) => onFrameDataChange && onFrameDataChange('properties.shape', e.target.value)}
                />
            </Grid> */}
            <Grid item xs={3}>
                <TextField label="A" variant="outlined" size="small" fullWidth value={frameData.properties?.aMeasurement || ''} onChange={(e) => onFrameDataChange && onFrameDataChange('properties.aMeasurement', e.target.value)} />
            </Grid>
            <Grid item xs={3}>
                <TextField label="B" variant="outlined" size="small" fullWidth value={frameData.properties?.bMeasurement || ''} onChange={(e) => onFrameDataChange && onFrameDataChange('properties.bMeasurement', e.target.value)} />
            </Grid>
            <Grid item xs={3}>
                <TextField label="ED" variant="outlined" size="small"  fullWidth value={frameData.properties?.edMeasurement || ''} onChange={(e) => onFrameDataChange && onFrameDataChange('properties.edMeasurement', e.target.value)} />
            </Grid>
            <Grid item xs={3}>
                <TextField label="DBL" variant="outlined" size="small"  fullWidth value={frameData.properties?.dblMeasurement || ''} onChange={(e) => onFrameDataChange && onFrameDataChange('properties.dblMeasurement', e.target.value)} />
            </Grid>
            <Grid item xs={3}>
                <TextField label="Eye" variant="outlined" size="small"  fullWidth value={frameData.properties?.eyeSize || ''} onChange={(e) => onFrameDataChange && onFrameDataChange('properties.eyeSize', e.target.value)} />
            </Grid>
            <Grid item xs={3}>
                <TextField label="Temp" variant="outlined" size="small"  fullWidth value={frameData.properties?.templeLength || ''} onChange={(e) => onFrameDataChange && onFrameDataChange('properties.templeLength', e.target.value)} />
            </Grid>
            {/* <Grid item xs={3}>
                <TextField label="CIRC" variant="outlined" size="small"  fullWidth value={frameData.properties?.circumference || ''} onChange={(e) => onFrameDataChange && onFrameDataChange('properties.circumference', e.target.value)} />
            </Grid>
            <Grid item xs={3}>
                <TextField label="BRDG" variant="outlined" size="small"  fullWidth value={frameData.properties?.bridgeSize || ''} onChange={(e) => onFrameDataChange && onFrameDataChange('properties.bridgeSize', e.target.value)} />
            </Grid> */}
            <Grid item xs={3}>
                <TextField
                    variant="filled"
                    size="small"
                    label="Price"
                    type='text'
                    
                    value={`${frameData.listPrice || '0.00'}`}
                    onChange={(e) => onFrameDataChange && onFrameDataChange('listPrice', e.target.value)}
                    //onChange={(e) => setFramePrice(e.target.value)}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                />
            </Grid>
        </Grid>
    );
}